import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { NodeProps } from '@xyflow/react';
import clsxm from '@/lib/clsxm';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWorkflowStore } from '@/lib/workflows/store';
import { getNodeCreditCost } from '@/lib/workflows/executor';
import { NodeExecutionStatus } from 'types';
import {
  getVideoModelsByInputType,
  getAspectRatiosForVideoModel,
  getDefaultDurationForModelTitle,
  getDefaultResolutionForModelTitle,
  getDurationsForVideoModel,
  getResolutionsForVideoModel,
  getVideoModelCredits,
  modelSupportsAudioToggle,
  videoModelsWithDescription,
  AspectRatioType,
  DurationType,
  ResolutionType,
  resolutionLabels,
} from '@/constants/videoModels';

import useLocale from 'next-translate/useTranslation';
import { TipTapPromptEditor } from '../TipTapPromptEditor';
import Button from '@/components/buttons/Button';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import { NodeActionsDropdown } from './NodeActionsDropdown';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface TextToVideoNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
}

export function TextToVideoNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
}: TextToVideoNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!Boolean(data.result));
  const [isLandscape, setIsLandscape] = useState(false);

  const executionStatus =
    (data.status as NodeExecutionStatus | undefined) || 'idle';
  const isRunning = executionStatus === 'running';

  // Get all text-to-video models
  const textToVideoModels = useMemo(
    () => getVideoModelsByInputType('text'),
    [],
  );

  const configSource =
    (data.config as {
      model?: string;
      prompt?: string;
      duration?: string;
      aspectRatio?: string;
      resolution?: string;
      generateAudio?: boolean;
    }) || {};
  const config: {
    model: string;
    prompt: string;
    duration?: string;
    aspectRatio: string;
    resolution?: string;
    generateAudio?: boolean;
  } = {
    model: configSource.model || textToVideoModels[0]?.title || 'Veo 3.1 Fast',
    prompt: configSource.prompt || '',
    duration: configSource.duration,
    aspectRatio: configSource.aspectRatio || '16:9',
    resolution: configSource.resolution,
    generateAudio: configSource.generateAudio,
  };

  // Track previous result to detect when it changes
  const prevResultRef = useRef(data.result);

  // Auto-collapse settings when result is generated (only when result changes)
  useEffect(() => {
    const resultChanged = prevResultRef.current !== data.result;
    if (resultChanged && !!data.result && showSettings && !isRunning) {
      setShowSettings(false);
    }
    prevResultRef.current = data.result;
  }, [data.result, isRunning, showSettings]);

  // Auto-expand settings when running
  useEffect(() => {
    if (isRunning && !showSettings) {
      setShowSettings(true);
    }
  }, [isRunning, showSettings]);

  // Hide result if running
  const shouldShowResult = !!data.result && !isRunning;

  // Extract video URL for dropdown
  const videoUrl = useMemo(() => {
    const resultValue = data.result;
    if (typeof resultValue === 'string') return resultValue;
    if (resultValue && typeof resultValue === 'object') {
      return (resultValue as { videoUrl?: string }).videoUrl;
    }
    return undefined;
  }, [data.result]);

  // Get available aspect ratios and durations for selected model
  const availableAspectRatios = useMemo(
    () => getAspectRatiosForVideoModel(config.model),
    [config.model],
  );

  const availableDurations = useMemo(
    () => getDurationsForVideoModel(config.model),
    [config.model],
  );

  const availableResolutions = useMemo(
    () => getResolutionsForVideoModel(config.model),
    [config.model],
  );

  const showAudioToggle = useMemo(
    () => modelSupportsAudioToggle(config.model),
    [config.model],
  );

  // Reset aspect ratio, duration, and resolution if current selection is invalid for the model
  useEffect(() => {
    let needsUpdate = false;
    const updates = { ...config };

    if (
      !availableAspectRatios.includes(config.aspectRatio as AspectRatioType)
    ) {
      updates.aspectRatio = availableAspectRatios[0] || '16:9';
      needsUpdate = true;
    }

    if (
      availableDurations.length > 0 &&
      (!config.duration ||
        !availableDurations.includes(config.duration as DurationType))
    ) {
      updates.duration =
        getDefaultDurationForModelTitle(config.model) ?? availableDurations[0];
      needsUpdate = true;
    }

    if (availableResolutions.length > 0) {
      if (
        !config.resolution ||
        !availableResolutions.includes(config.resolution as ResolutionType)
      ) {
        updates.resolution =
          getDefaultResolutionForModelTitle(config.model) ??
          availableResolutions[0];
        needsUpdate = true;
      }
    } else if (config.resolution) {
      updates.resolution = undefined;
      needsUpdate = true;
    }

    // Reset audio toggle on model change
    if (config.generateAudio !== undefined) {
      updates.generateAudio = undefined;
      needsUpdate = true;
    }

    if (needsUpdate) {
      updateNodeData(id, { config: updates });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.model]);

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        model: e.target.value,
      },
    });
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        duration: e.target.value,
      },
    });
  };

  const handleAspectRatioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        aspectRatio: e.target.value,
      },
    });
  };

  const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        resolution: e.target.value,
      },
    });
  };

  const handleGenerateAudioChange = (value: boolean) => {
    updateNodeData(id, {
      config: {
        ...config,
        generateAudio: value ? undefined : false,
      },
    });
  };

  const handlePromptChange = (value: string) => {
    updateNodeData(id, {
      config: {
        ...config,
        prompt: value,
      },
    });
  };

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        return {
          id: sourceNode?.id || '',
          label: (sourceNode?.data?.label as string) || 'Node',
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  // Calculate credit cost for this node
  const creditCost = useMemo(() => {
    return getVideoModelCredits(
      config.model,
      config.duration,
      config.resolution as ResolutionType | undefined,
      config.generateAudio,
    );
  }, [config.model, config.duration, config.resolution, config.generateAudio]);

  return (
    <BaseNode
      title={(data.label as string) || t('Text to Video')}
      icon={
        <svg
          className='w-4 h-4 text-cyan-600'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={executionStatus}
      selected={selected}
      nodeId={nodeId || id}
      onRunNode={onRunNode}
      customInputs={createInputHandle('text', 'prompt', 50)}
      customOutputs={
        <>
          {createOutputHandle('image', 'first-frame', 30)}
          {createOutputHandle('image', 'last-frame', 60)}
          {createOutputHandle('video', 'video', 90)}
        </>
      }
      hideToolbarActions
      customHeaderButtons={
        <>
          {(data as any)?.result && !isRunning && (
            <SimpleTooltip
              title={showSettings ? t('Hide settings') : t('Show settings')}
              side='top'
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}
                className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
              >
                <svg
                  className='w-5 h-5 text-gray-600 dark:text-gray-300'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                  <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                </svg>
              </button>
            </SimpleTooltip>
          )}
          <NodeActionsDropdown
            nodeId={nodeId || id}
            nodeTitle={(data.label as string) || t('Text to Video')}
            mediaUrl={videoUrl}
            mediaType='video'
            filename='generated-video.mp4'
          />
        </>
      }
    >
      <div className={showSettings || !data.result ? 'w-[400px]' : ''}>
        {/* Collapsible Settings Section */}
        <div
          className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
            showSettings ? '' : 'hidden'
          }`}
        >
          {/* Model Selection */}
          <div>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('Model')}
            </label>
            <select
              value={config.model}
              onChange={handleModelChange}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
            >
              {textToVideoModels.map((model) => (
                <option key={model.id} value={model.title}>
                  {model.title}
                </option>
              ))}
            </select>
          </div>

          {/* Aspect Ratio */}
          <div>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('Aspect Ratio')}
            </label>
            <select
              value={config.aspectRatio}
              onChange={handleAspectRatioChange}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
            >
              {availableAspectRatios.map((ratio) => (
                <option key={ratio} value={ratio}>
                  {ratio}
                </option>
              ))}
            </select>
          </div>

          {/* Duration */}
          {availableDurations.length > 0 && (
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Duration')}
              </label>
              <select
                value={config.duration}
                onChange={handleDurationChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
              >
                {availableDurations.map((duration) => (
                  <option key={duration} value={duration}>
                    {duration}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Resolution */}
          {availableResolutions.length > 1 && (
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Resolution')}
              </label>
              <select
                value={config.resolution || availableResolutions[0]}
                onChange={handleResolutionChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
              >
                {availableResolutions.map((res) => (
                  <option key={res} value={res}>
                    {t(resolutionLabels[res]) || res}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Audio Toggle */}
          {showAudioToggle && (
            <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-slate-700/30'>
              <div className='flex items-center gap-2'>
                <svg
                  className='h-4 w-4 text-gray-500 dark:text-gray-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth='1.5'
                  stroke='currentColor'
                >
                  {config.generateAudio !== false ? (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z'
                    />
                  ) : (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.757 3.63 8.25 4.51 8.25H6.75z'
                    />
                  )}
                </svg>
                <span className='text-xs font-medium text-gray-700 dark:text-gray-300'>
                  {t('Audio')}
                </span>
                {config.generateAudio === false &&
                  (() => {
                    const m = videoModelsWithDescription.find(
                      (v) => v.title === config.model,
                    );
                    const mult = m?.options.audioDisabledCreditsMultiplier;
                    return mult !== undefined && mult < 1 ? (
                      <span className='inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'>
                        {Math.round((1 - mult) * 100)}
                        {t('% off')}
                      </span>
                    ) : null;
                  })()}
              </div>
              <button
                type='button'
                role='switch'
                aria-checked={config.generateAudio !== false}
                onClick={() =>
                  handleGenerateAudioChange(config.generateAudio === false)
                }
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                  config.generateAudio !== false
                    ? 'bg-primary-600'
                    : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    config.generateAudio !== false
                      ? 'translate-x-4'
                      : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Prompt */}
          <div>
            <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {t('Prompt')}
            </label>
            <TipTapPromptEditor
              value={config.prompt || ''}
              onChange={handlePromptChange}
              connectedNodes={getConnectedTextNodes()}
              placeholder={t(
                'Describe the video... Type @ to reference connected nodes',
              )}
            />
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
              {t('Type @ to reference connected nodes')}
            </p>
          </div>

          {/* Run Button (inside settings) */}
          <div className='pt-1'>
            <Button
              className='w-full justify-center'
              variant='outline'
              isLoading={isRunning}
              onClick={(e) => {
                e.stopPropagation();
                onRunNode?.(nodeId || id);
              }}
            >
              {data.result ? (
                <>
                  <RefreshIcon className='w-4 h-4 mr-2' />
                  <span>
                    {t('Regenerate')} ({creditCost}{' '}
                    {creditCost === 1 ? t('credit') : t('credits')})
                  </span>
                </>
              ) : (
                <>
                  <PlayIcon className='w-4 h-4 mr-2' />
                  <span>
                    {t('Run')} ({creditCost}{' '}
                    {creditCost === 1 ? t('credit') : t('credits')})
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Result Preview - Prominent Display */}
        {shouldShowResult && (
          <div
            className={clsxm(
              showSettings
                ? 'w-full px-4 pb-3'
                : isLandscape
                  ? 'w-[700px]'
                  : 'w-[420px]',
            )}
          >
            {(() => {
              const resultValue = (data as { result?: unknown })?.result;
              const asVideoResult = (
                val: unknown,
              ): val is {
                videoUrl?: string;
              } => typeof val === 'object' && val !== null;
              const videoSrc =
                typeof resultValue === 'string'
                  ? (resultValue as string)
                  : asVideoResult(resultValue)
                    ? resultValue.videoUrl
                    : undefined;
              return (
                <>
                  {videoSrc && (
                    <div className='relative group rounded-md overflow-hidden'>
                      <video
                        src={videoSrc}
                        controls
                        className='w-full h-auto object-cover'
                        autoPlay={!isMobile}
                        preload={isMobile ? 'metadata' : 'auto'}
                        muted
                        playsInline
                        loop
                        onLoadedMetadata={(e) => {
                          const video = e.currentTarget;
                          setIsLandscape(video.videoWidth > video.videoHeight);
                        }}
                      />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Error Display */}
        {!!data.error && (
          <div className='mx-3 mb-3 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2 py-2 rounded'>
            <p className='text-xs text-red-600 dark:text-red-400'>
              {String(data.error)}
            </p>
          </div>
        )}
      </div>
    </BaseNode>
  );
}
